'use client';

import { useState } from 'react';
import { toast } from '~/utils/toast';
import { Check, MessageCircle } from 'lucide-react';
import Button from '../Button';
import Chip from '../Chip';
import Quote from '../Quote';
import Tooltip from '../Tooltip';
import IgnoreFeedback from './IgnoreFeedback';
import { type PolicyCardProps } from './PolicyCard';
import Suggestions from './Suggestions';
import ThumbsUp from './ThumbsUp';
import {
  FeedbackItemActions,
  FeedbackItemFrame,
  FeedbackItemHeader,
  FeedbackModeChips,
  FeedbackNewChip,
  FeedbackRiskChip,
  buildFeedbackMeta,
  formatVideoTime,
} from './FeedbackItemFrame';
import { cn } from '~/lib/utils';
import { getContentMediumLabel } from './FeedbackSource';
import { type FeedbackListExitHandlers } from './FeedbackList';

export type ApprovalStatus = 'Approved' | 'Likely Approved';

export function ApprovalComponent({ approval }: { approval: ApprovalStatus }) {
  return <Chip level={approval === 'Approved' ? 'success' : 'warning'} text={approval} />;
}

type FeedbackCardProps = {
  title: string;
  description: string;
  confirmButton?: React.ReactNode;
  onResolve: () => Promise<void>;
  videoStartTime?: number;
  approvalStatus: ApprovalStatus | undefined;
  isClaim: boolean;
  isPublicView?: boolean;
  isShadowFeedback?: boolean;
  shadowModelName?: string;
  isPrimaryFeedback?: boolean;
  primaryModelName?: string;
  isInShadowMode?: boolean;
  alreadyMarked?: boolean;
} & Pick<
  PolicyCardProps,
  | 'policy'
  | 'brand'
  | 'reloadDoc'
  | 'documentId'
  | 'isTest'
  | 'isMultiPages'
  | 'isNew'
  | 'contentMedium'
  | 'color'
  | 'feedbackLocation'
  | 'onComment'
  | 'suggestions'
  | 'onClick'
  | 'children'
  | 'riskLevel'
  | 'feedbackId'
  | 'evaluationType'
  | 'isResolvedFeedback'
  | 'isCompactView'
  | 'onRemove'
> &
  Partial<FeedbackListExitHandlers>;

export default function FeedbackCard(props: FeedbackCardProps) {
  const [isResolving, setIsResolving] = useState(false);
  const [justResolved, setJustResolved] = useState(false);
  const [markedLocally, setMarkedLocally] = useState(false);
  const thumbsGreyed = !!props.alreadyMarked || markedLocally;
  const hideTeachActions = props.isTest || props.isResolvedFeedback;

  const sourceLabel = getContentMediumLabel(props.contentMedium);
  const meta = buildFeedbackMeta([
    props.videoStartTime ? formatVideoTime(props.videoStartTime) : null,
    props.isMultiPages ? `Page ${(props.feedbackLocation.pageNumber ?? 0) + 1}` : null,
  ]);

  return (
    <FeedbackItemFrame
      onClick={props.onClick}
      isShadowFeedback={props.isShadowFeedback}
      isPrimaryFeedback={props.isPrimaryFeedback}
      isResolvedFeedback={props.isResolvedFeedback}
      isCompactView={props.isCompactView}
      className={cn(
        justResolved ? 'opacity-70' : undefined,
        props.isRowExiting?.(props.feedbackId ?? '') && 'pointer-events-none',
      )}
    >
      <FeedbackItemHeader
        title={props.title}
        color={props.color}
        badge={sourceLabel || undefined}
        meta={meta}
        isCompactView={props.isCompactView}
        trailing={
          <>
            <FeedbackModeChips
              isInShadowMode={props.isInShadowMode}
              isPrimaryFeedback={props.isPrimaryFeedback}
              isShadowFeedback={props.isShadowFeedback}
            />
            <FeedbackNewChip isNew={props.isNew} />
            {props.approvalStatus ? (
              <ApprovalComponent approval={props.approvalStatus} />
            ) : (
              <FeedbackRiskChip riskLevel={props.riskLevel} />
            )}
          </>
        }
      />

      {props.description ? (
        <Quote text={props.description} className={props.isCompactView ? 'text-xs' : undefined} />
      ) : null}
      {props.children}
      {!props.isShadowFeedback && (
        <FeedbackItemActions
          leading={
            <>
              {!hideTeachActions &&
                !props.isPublicView &&
                props.policy &&
                props.feedbackId &&
                props.description &&
                props.isClaim &&
                !props.policy.hasVisionPrompt && (
                  <>
                    <ThumbsUp
                      concatenateFeedbacks={[]}
                      feedbackId={props.feedbackId}
                      type={'claim'}
                      alreadyMarked={props.alreadyMarked}
                      onMarked={() => setMarkedLocally(true)}
                    />
                    <IgnoreFeedback
                      feedbackId={props.feedbackId}
                      listRowId={props.feedbackId}
                      policy={props.policy}
                      text={props.description}
                      reloadDoc={props.reloadDoc}
                      brand={props.brand}
                      documentId={props.documentId}
                      concatenateFeedbacks={[]}
                      beginExit={props.beginExit}
                      restoreExit={props.restoreExit}
                      evaluationType={props.evaluationType}
                      type="claim"
                      disabled={thumbsGreyed}
                    />
                  </>
                )}
              {!hideTeachActions && !props.isPublicView && props.confirmButton}
              {props.onComment && !hideTeachActions && !props.isPublicView && (
                <Tooltip content="Comment" hideIcon>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Comment"
                    onClick={props.onComment}
                  >
                    <MessageCircle className="size-4" strokeWidth={2} />
                  </Button>
                </Tooltip>
              )}
              {props.suggestions?.length && !hideTeachActions && !props.isPublicView ? (
                <Suggestions
                  suggestions={props.suggestions}
                  feedbackId={props.feedbackId}
                  isCompactView={props.isCompactView}
                />
              ) : null}
            </>
          }
          trailing={
            !props.isTest && !props.isPublicView ? (
              <Button
                type="button"
                size="sm"
                variant={props.isResolvedFeedback || justResolved ? 'outline' : 'default'}
                disabled={isResolving || justResolved}
                isLoading={isResolving}
                onClick={async () => {
                  if (!props.feedbackId || !props.beginExit || !props.restoreExit) return;
                  if (isResolving || justResolved) return;

                  const feedbackId = props.feedbackId;
                  const isUnresolve = props.isResolvedFeedback;

                  if (!isUnresolve) {
                    setJustResolved(true);
                  }
                  const mutation = props.onResolve();
                  props.beginExit(feedbackId, isUnresolve ? 'remove' : 'resolve', mutation);

                  try {
                    setIsResolving(true);
                    await mutation;
                  } catch (err) {
                    console.error(err);
                    props.restoreExit(feedbackId);
                    setJustResolved(false);
                    toast.error(
                      isUnresolve
                        ? "Couldn't unresolve this finding"
                        : "Couldn't resolve this finding",
                    );
                  } finally {
                    setIsResolving(false);
                  }
                }}
              >
                {justResolved ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="size-3.5" strokeWidth={2.5} aria-hidden />
                    Resolved
                  </span>
                ) : props.isResolvedFeedback ? (
                  'Unresolve'
                ) : (
                  'Resolve'
                )}
              </Button>
            ) : null
          }
        />
      )}
    </FeedbackItemFrame>
  );
}
